import React, { useEffect, useState, useRef } from 'react';
import { FaceLivenessDetector } from '@aws-amplify/ui-react-liveness';
import { Loader, ThemeProvider } from '@aws-amplify/ui-react';
import { Amplify } from 'aws-amplify';
import { fetchAuthSession } from 'aws-amplify/auth';
import '@aws-amplify/ui-react/styles.css';
import AWS from 'aws-sdk'

import awsexports from './aws-exports';

Amplify.configure(awsexports);

// Use the Amplify project region (from aws-exports) as the Rekognition region.
// Fallback is hard-coded to the current deployment region.
const REKOGNITION_REGION = awsexports.aws_project_region || 'ap-northeast-1';

// Helper to get Rekognition client with Cognito credentials
const getRekognitionClient = async () => {
    const session = await fetchAuthSession();
    const credentials = session.credentials;

    if (!credentials) {
        throw new Error('Failed to obtain AWS credentials from Cognito Identity Pool');
    }

    return new AWS.Rekognition({
        region: REKOGNITION_REGION,
        credentials: new AWS.Credentials({
            accessKeyId: credentials.accessKeyId,
            secretAccessKey: credentials.secretAccessKey,
            sessionToken: credentials.sessionToken,
        }),
    });
};

export default function App() {

    const livenessTimerRef = useRef(null);
    // reference variables
    const sessionIdRef = useRef(null);

    // useState variables
    const [loading, setLoading] = useState(false);
    // eslint-disable-next-line no-unused-vars
    const [smileScore, setSmileScore] = useState(0);
    const [showLivenessDetector, setShowLivenessDetector] = useState(false);
    const [UIStatus, setUIStatus] = useState(0);
    const [smileImage, setSmileImage] = useState("");
    const [showErrorScreen, setShowErrorScreen] = useState(false);

    useEffect(() => {
        return () => {
            if (livenessTimerRef.current) {
                clearTimeout(livenessTimerRef.current);
            }
        };
    }, []);

    // Function - handling the liveness start
    const _handleStart = async () => {

        if (livenessTimerRef.current) {
            clearInterval(livenessTimerRef.current);
        }
        // setting timer for 1 min
        livenessTimerRef.current = setInterval(() => {
            setShowLivenessDetector(false);
            setUIStatus(0);
            setShowErrorScreen(true);
            sessionIdRef.current = null;
            clearTimeout(livenessTimerRef.current);
            livenessTimerRef.current = 0;
        }, 60000);
        await fetchCreateLiveness();
        setShowLivenessDetector(true);
    }

    const handleAnalysisComplete = async () => {
        const rekognition = await getRekognitionClient();
        await rekognition.getFaceLivenessSessionResults({ SessionId: sessionIdRef.current }).promise().then((result) => {
            let image = JSON.stringify(result.ReferenceImage.Bytes);
            let score = result.Confidence.toFixed();
            if (result.Status === 'SUCCEEDED') {
                setShowLivenessDetector(false);
                if (UIStatus === 1) {
                    setSmileScore(result.Confidence.toFixed());
                    setSmileImage(image);
                    if (window.opener) {
                        window.opener.postMessage(
                            {
                                type: "liveness_done",
                                payload: {
                                    smileScore: score,
                                    smileImage: image,
                                }
                            },
                            "*" // OR replace with specific origin for security
                        );
                    }

                    window.history.pushState(
                        {}, "",
                        `/?smile-score=${score}&smile-image=${image}`);
                    // setUIStatus(2);
                }

            }
        }).catch(err => {
            console.log('results error |', err);
        });

    };

    // Function  cancelling liveness
    const _cancelLiveness = () => {
        setShowLivenessDetector(false);
        setUIStatus(0);
    }

    // Restart liveness check
    const _restartLivenessCheck = () => {
        setUIStatus(1);
        setShowErrorScreen(false);
    }


    const handleRestart = async () => {
        setShowLivenessDetector(false);
        setUIStatus(0);
        setShowErrorScreen(true);
        sessionIdRef.current = null;
        if (livenessTimerRef.current) {
            clearTimeout(livenessTimerRef.current);
            livenessTimerRef.current = null;
        }
    }

    const fetchCreateLiveness = async () => {
        try {
            const rekognition = await getRekognitionClient();
            const params = {};

            const response = await rekognition.createFaceLivenessSession(params).promise();
            console.log('response |', response);

            sessionIdRef.current = response.SessionId;
        } catch (error) {
            console.error('Error creating session:', error);
        }
        setLoading(false);
    };

    return (
        <ThemeProvider>
            <div style={{ textAlign: 'center', padding: '20px' }}>
                {loading ? (
                    <Loader />
                ) : (
                    <div style={{
                        maxWidth: '600px',
                        margin: 'auto',
                        padding: '20px',
                        borderRadius: '10px',
                        backgroundColor: '#fff',
                        border: '2px solid #ddd',
                        boxShadow: '0 4px 8px rgba(0, 0, 0, 0.1)',
                    }}>
                        {
                            (showLivenessDetector === true) ? (
                                <FaceLivenessDetector sessionId={sessionIdRef.current}
                                    region={REKOGNITION_REGION}
                                    disableStartScreen={true}
                                    onAnalysisComplete={handleAnalysisComplete}
                                    onUserCancel={_cancelLiveness}
                                    components={{
                                        ErrorView: () => {
                                            return (
                                                <div>
                                                    <p>We are unable to verify your details now. Please try again.</p>
                                                    <button onClick={_restartLivenessCheck}>Try again</button>
                                                </div>
                                            )
                                        }
                                    }}
                                    onError={(error) => {
                                        console.log('error', error);
                                        handleRestart();
                                    }} />
                            ) : UIStatus === 0 ? (
                                <div>
                                    {
                                        showErrorScreen === true ?
                                            <div>
                                                <p>We are unable to verify your details now. Please try again.</p>
                                                <button onClick={_restartLivenessCheck}>Try again</button>
                                            </div>
                                            :
                                            <div>
                                                <h1>Face Liveness Check</h1>
                                                <p>Please follow instructions to complete the liveness check</p>
                                                <button onClick={() => setUIStatus(1)}>Start Liveness Check</button>
                                            </div>
                                    }

                                </div>
                            ) : (
                                <div>
                                    {
                                        smileImage === "" ?
                                            <div>
                                                <h1>Smile Test</h1>
                                                <p>Please smile to the camera</p>
                                                <button onClick={_handleStart}>Start</button>
                                            </div>
                                            :
                                            <div>
                                                <h1>Loading...</h1>
                                            </div>
                                    }
                                </div>
                            )
                        }
                    </div>
                )}
            </div>
        </ThemeProvider>
    );
}